import {
  Box,
  Button,
  Container,
  Fade,
  IconButton,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  AlertComponent,
  DialogComponent,
  NavBar,
} from "../../components";
import EditIcon from "@mui/icons-material/Edit";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { Link, useNavigate } from "react-router-dom";
import { useContext, useEffect, useMemo, useState } from "react";
import DesignerContext from "../../context/DesignerContext";
import { usePermissions } from "../../hooks";
import { PullingDesignProps } from "../../interfaces/interfaces";
import {
  ListColumn,
  ListPageLayout,
} from "../../components/shared/ListPageLayout";

const DEFAULT_PAGE_SIZE = 10;

// Sorts the FULL array using the same accessor a column declares via
// `sortAccessor`, before pagination slices it — mirrors ListPageLayout's own
// internal (uncontrolled) sort algorithm exactly, but is invoked here, over
// the complete filtered dataset, because `fetchPullingList()` fetches the
// entire pulling-design list in one call (no server-side pagination), so
// sorting only the already-sliced page (ListPageLayout's uncontrolled
// default) would silently leave the rest of the dataset unsorted relative to
// the visible page. See TableWellDetail.tsx for the template this mirrors.
function sortRows<T>(
  rows: T[],
  sortBy: string | null,
  sortDir: "asc" | "desc",
  columns: ListColumn<T>[],
): T[] {
  if (!sortBy) return rows;
  const col = columns.find((c) => c.key === sortBy);
  if (!col?.sortAccessor) return rows;
  return [...rows].sort((a, b) => {
    const av = col.sortAccessor!(a);
    const bv = col.sortAccessor!(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });
}

export default function PullingDesignList() {
  const {
    fetchPullingList,
    fetchDataPulling,
    handleDeletePulling,
    pullingList,
    isLoading,
    isSuccess,
    isError,
  } = useContext(DesignerContext);
  const { canManagePulling } = usePermissions();
  const navigate = useNavigate();
  const [idToDelete, setIdToDelete] = useState("");
  const [isOpenAlert, setIsOpenAlert] = useState(false);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    fetchPullingList();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSortChange = (key: string) => {
    setSortDir((prev) => (sortBy === key && prev === "asc" ? "desc" : "asc"));
    setSortBy(key);
  };

  const columns: ListColumn<PullingDesignProps>[] = [
    {
      key: "client",
      header: "Client",
      render: (r) => (
        <Link to={`/pulling/${r.id}`} target="_blank" rel="noopener noreferrer">
          {r.well?.client?.name}
        </Link>
      ),
      sortAccessor: (r) => r.well?.client?.name ?? "",
      width: "1.4fr",
    },
    {
      key: "well",
      header: "Well",
      render: (r) => r.well?.name || "",
      sortAccessor: (r) => r.well?.name ?? "",
      width: "1.2fr",
    },
    {
      key: "description",
      header: "Description",
      render: (r) => `${r.pullingName ?? ""} - ${r.customName ?? ""}`,
      sortAccessor: (r) => `${r.pullingName ?? ""} - ${r.customName ?? ""}`,
      width: "1.6fr",
    },
  ];

  const filteredList = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pullingList;
    return pullingList.filter((r) => {
      const haystack = [
        r.well?.client?.name,
        r.well?.name,
        r.pullingName,
        r.customName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [pullingList, search]);

  // Sort the FULL filtered array first (see sortRows' comment above for why),
  // THEN slice to the current page — so paging through a sorted list is
  // correct relative to the whole dataset, not just whatever rows happened to
  // land on that page beforehand.
  const sortedList = sortRows(filteredList, sortBy, sortDir, columns);
  const pagedList = sortedList.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <>
      <NavBar title="Pulling Reports" buttons={[]} />
      <Box
        sx={{
          marginTop: "80px",
          minHeight: "calc(100vh - 80px)",
          background:
            "linear-gradient(135deg, rgba(241, 236, 231, 0.8) 0%, rgba(241, 236, 231, 0.4) 100%)",
          padding: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="xl">
          <Fade in={true} timeout={600}>
            <Paper
              elevation={3}
              sx={{
                padding: { xs: 2, md: 4 },
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(140, 150, 169, 0.1)",
                boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
              }}
            >
              <ListPageLayout<PullingDesignProps>
                title="Pulling Reports"
                columns={columns}
                rows={pagedList}
                getRowId={(r) => r.id}
                rowsLoading={isLoading}
                isError={isError}
                errorMessage="Pulling reports could not be loaded."
                searchValue={search}
                onSearchChange={setSearch}
                searchPlaceholder="Search pulling reports…"
                sortBy={sortBy}
                sortDir={sortDir}
                onSortChange={handleSortChange}
                page={page}
                pageSize={pageSize}
                totalCount={filteredList.length}
                onPageChange={setPage}
                onPageSizeChange={(newPageSize) => {
                  setPageSize(newPageSize);
                  setPage(0);
                }}
                toolbarActions={
                  canManagePulling ? (
                    <Button
                      startIcon={<AddBoxIcon />}
                      onClick={() => navigate("/pulling/0")}
                    >
                      New
                    </Button>
                  ) : undefined
                }
                rowActions={(r) => (
                  <>
                    {canManagePulling && (
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => navigate(`/pulling/${r.id}`)}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canManagePulling && (
                      <Tooltip title="Duplicate">
                        <IconButton
                          onClick={() => {
                            fetchDataPulling(r.id);
                            navigate("/pulling/0");
                          }}
                          size="small"
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Print">
                      <IconButton
                        onClick={() => navigate(`/pulling/${r.id}?pdf=true`)}
                        size="small"
                      >
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canManagePulling && (
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => {
                            setIdToDelete(r.id.toString());
                            setIsOpenAlert(true);
                          }}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                )}
              />
            </Paper>
          </Fade>
        </Container>
      </Box>
      {isSuccess && <AlertComponent type="success" />}
      {isError && <AlertComponent type="error" />}
      {isOpenAlert && (
        <DialogComponent
          type="error"
          modalContent={<div>Are you sure you want to remove this item?</div>}
          onAccept={() => {
            handleDeletePulling(idToDelete);
            setIdToDelete("");
            setIsOpenAlert(false);
          }}
          onCancel={() => {
            setIsOpenAlert(false);
            setIdToDelete("");
          }}
        />
      )}
    </>
  );
}
